import type { JSX } from "react";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router";
import { toast } from "sonner";
import { Save, X } from "lucide-react";
import type {
  RecipeIngredient,
  RecipeSource,
  RecipeStep,
  RecipeTaxonomyRefs,
} from "@recipevault/shared";
import { useRecipe } from "./useRecipe.js";
import { useAuth } from "../auth/AuthProvider.js";
import { sync } from "../../sync/SyncService.js";
import { ApiError, friendlyError } from "../../shared/http.js";
import { PhotoUploader } from "./PhotoUploader.js";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import { Label } from "@/ui/label.js";
import { Textarea } from "@/ui/textarea.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select.js";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/ui/tabs.js";
import { IngredientList } from "./IngredientList.js";
import { StepList } from "./StepList.js";
import { TaxonomyPicker } from "./TaxonomyPicker.js";
import Rating from "./Rating.js";
import type { RatingValue } from "./Rating.js";
const MarkdownView = lazy(() => import("./MarkdownView.js"));

const EMPTY_TAXONOMY: RecipeTaxonomyRefs = {
  tagIds: [],
  categoryIds: [],
  cuisineId: null,
  dietFlags: [],
};

function toIntOrNull(s: string): number | null {
  if (s.trim() === "") return null;
  const n = parseInt(s, 10);
  if (Number.isNaN(n)) return null;
  return n;
}

function toTrimmedOrNull(s: string): string | null {
  const t = s.trim();
  return t === "" ? null : t;
}

export function RecipeEditor(): JSX.Element {
  const params = useParams<{ id: string }>();
  const isEdit = Boolean(params.id);
  const { recipe, status } = useRecipe(params.id);
  const navigate = useNavigate();
  const { me } = useAuth();
  const householdId =
    me?.user.currentHouseholdId ?? me?.memberships[0]?.householdId ?? "";

  const [title, setTitle] = useState("");
  const [source, setSource] = useState<RecipeSource>({ kind: "manual" });
  const [ingredients, setIngredients] = useState<RecipeIngredient[]>([]);
  const [steps, setSteps] = useState<RecipeStep[]>([]);
  const [taxonomy, setTaxonomy] = useState<RecipeTaxonomyRefs>(EMPTY_TAXONOMY);
  const [version, setVersion] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  // Plan 08 fields
  const [description, setDescription] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [servings, setServings] = useState<string>("");
  const [totalTimeMin, setTotalTimeMin] = useState<string>("");
  const [activeTimeMin, setActiveTimeMin] = useState<string>("");
  const [difficulty, setDifficulty] = useState<"easy" | "medium" | "hard" | "">("");
  const [rating, setRating] = useState<RatingValue>(null);

  // Hydrate the form EXACTLY ONCE per recipe id. `recipe` is a live Dexie query
  // (useLiveQuery) that emits a brand-new object on every write to the row —
  // your own photo upload, a 409 rollback to server truth, a partner edit pulled
  // by delta sync. If hydration re-ran on each of those, the user's in-progress
  // edits would be silently wiped (data loss). So we snapshot on first arrival
  // and guard with a ref keyed on the recipe id; later live-query changes never
  // clobber user-edited fields.
  const hydratedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isEdit || status !== "ready" || !recipe) return;
    if (hydratedIdRef.current === recipe.id) return;
    hydratedIdRef.current = recipe.id;
    setTitle(recipe.title);
    setSource(recipe.source);
    setIngredients(recipe.ingredients);
    setSteps(recipe.steps);
    setTaxonomy(recipe.taxonomy);
    setVersion(recipe.version);
    // Plan 08 fields
    setDescription(recipe.description ?? "");
    setNotes(recipe.notes ?? "");
    setServings(recipe.servings != null ? String(recipe.servings) : "");
    setTotalTimeMin(recipe.totalTimeMin != null ? String(recipe.totalTimeMin) : "");
    setActiveTimeMin(recipe.activeTimeMin != null ? String(recipe.activeTimeMin) : "");
    setDifficulty(recipe.difficulty ?? "");
    setRating(recipe.rating);
  }, [isEdit, status, recipe]);

  // Keep ONLY the optimistic-concurrency `version` in sync with the live row
  // after the initial hydration. This covers the row being bumped underneath us
  // (e.g. your own photo upload bumps `version`) without touching any field the
  // user may be editing. Re-saving then sends the current server version and
  // avoids a spurious 409. We deliberately do NOT mirror title/ingredients/etc:
  // those belong to the user once the form is hydrated.
  useEffect(() => {
    if (!isEdit || status !== "ready" || !recipe) return;
    if (hydratedIdRef.current !== recipe.id) return;
    setVersion((prev) => (prev !== null && recipe.version > prev ? recipe.version : prev));
  }, [isEdit, status, recipe]);

  if (isEdit && status === "loading") return <p className="text-zinc-500 text-sm">Loading…</p>;
  if (isEdit && status === "not-found") {
    return (
      <div className="py-12 text-center">
        <div className="text-zinc-300 text-lg font-medium mb-2">Recipe not found</div>
        <Link to="/recipes" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 text-sm">
          Back to library
        </Link>
      </div>
    );
  }

  const titleTrimmed = title.trim();
  const totalTimeInt = toIntOrNull(totalTimeMin);
  const activeTimeInt = toIntOrNull(activeTimeMin);
  const timeRangeValid = !(totalTimeInt != null && activeTimeInt != null && activeTimeInt > totalTimeInt);
  const canSave = titleTrimmed.length > 0 && timeRangeValid && !saving;

  async function save(): Promise<void> {
    if (!canSave) return;
    setSaving(true);
    try {
      // Drop entirely-blank rows (user added a row but didn't type anything).
      // Default `name = raw` — the manual editor only exposes `raw`; `name`
      // is the shopping-list aggregation key, populated explicitly by Plan 08's
      // LLM import but absent from the manual UI.
      const cleanIngredients = ingredients
        .filter((i) => i.raw.trim().length > 0)
        .map((i) => {
          const raw = i.raw.trim();
          const name = (i.name ?? "").trim() || raw;
          return { ...i, raw, name };
        });
      const cleanSteps = steps
        .filter((s) => s.text.trim().length > 0)
        .map((s) => ({ ...s, text: s.text.trim() }));

      if (isEdit && params.id) {
        // Explicit guard: an edit with no known version would otherwise fall
        // through to createRecipeOptimistic below and silently DUPLICATE the
        // recipe. The version should always be hydrated by the time Save is
        // enabled; if it isn't, surface an error instead of forking the data.
        if (version === null) {
          throw new Error("Still loading this recipe — please wait a moment and try again.");
        }
        const r = await sync.patchRecipeOptimistic(params.id, {
          version,
          title: titleTrimmed,
          source,
          ingredients: cleanIngredients,
          steps: cleanSteps,
          taxonomy,
          description: toTrimmedOrNull(description),
          notes: toTrimmedOrNull(notes),
          servings: toIntOrNull(servings),
          totalTimeMin: totalTimeInt,
          activeTimeMin: activeTimeInt,
          difficulty: difficulty === "" ? null : difficulty,
          rating,
        });
        toast.success(`Saved '${r.title}'`);
        void navigate(`/recipes/${r.id}`);
      } else {
        const r = await sync.createRecipeOptimistic({
          title: titleTrimmed,
          source,
          ingredients: cleanIngredients,
          steps: cleanSteps,
          taxonomy,
          description: toTrimmedOrNull(description),
          notes: toTrimmedOrNull(notes),
          servings: toIntOrNull(servings),
          totalTimeMin: totalTimeInt,
          activeTimeMin: activeTimeInt,
          difficulty: difficulty === "" ? null : difficulty,
          rating,
        });
        toast.success(`Created '${r.title}'`);
        void navigate(`/recipes/${r.id}`);
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === "VERSION_CONFLICT") {
        // Spec §3.4: PRESERVE the user's in-progress edits and let them re-save
        // against the new version. SyncService already rolled Dexie back to
        // server truth AND already toasted ("This recipe was updated. Please
        // review and try again.") — so we must NOT toast again (one message),
        // and we must NOT let the live-query rollback re-hydrate the form
        // (the hydratedIdRef guard prevents that). We only refresh the version
        // we'll send on the next PATCH, from the server's current item which
        // the backend echoes in responseBody.current — so the retry succeeds.
        const body = e.responseBody;
        const current =
          body && typeof body === "object" && "current" in body
            ? (body as { current?: { version?: unknown } }).current
            : undefined;
        if (current && typeof current.version === "number") {
          setVersion(current.version);
        }
      } else {
        toast.error(friendlyError(e));
      }
    } finally {
      setSaving(false);
    }
  }

  function discard(): void {
    // Replace, don't push — the editor was a modal-ish operation, not a page.
    // Pushing would leave /recipes/{id}/edit in history; Back would land back
    // on the editor we just dismissed instead of the recipe list.
    if (isEdit && params.id) void navigate(`/recipes/${params.id}`, { replace: true });
    else void navigate("/", { replace: true });
  }

  return (
    <>
      <header className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">
          {isEdit ? "Edit recipe" : "New recipe"}
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={discard} disabled={saving}>
            <X className="mr-2 h-4 w-4" aria-hidden />
            Discard
          </Button>
          <Button size="sm" onClick={() => { void save(); }} disabled={!canSave}>
            <Save className="mr-2 h-4 w-4" aria-hidden />
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </header>

      {/* Section: Basics — source first so Plan 08 imports (URL/text paste)
          can populate title + rest of the form via the LLM after the user
          picks their source. Manual entry just falls through to the title. */}
      <section className="space-y-3 border-b border-zinc-800 pb-4">
        <div className="space-y-1.5">
          <Label>Source</Label>
          <Select
            value={source.kind}
            onValueChange={(v: string) => {
              if (v === "url" || v === "text") {
                // Disabled — Imports arrive in Plan 08. Keep current value.
                return;
              }
              setSource({ kind: "manual" });
            }}
          >
            <SelectTrigger className="w-60" aria-label="Recipe source kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manual entry</SelectItem>
              <SelectItem value="url" disabled>
                Paste a URL — Plan 08
              </SelectItem>
              <SelectItem value="text" disabled>
                Paste text — Plan 08
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="recipe-title">Title</Label>
          <Input
            id="recipe-title"
            value={title}
            onChange={(e) => { setTitle(e.target.value); }}
            placeholder="Roasted Tomato Pasta"
            autoFocus={!isEdit}
          />
        </div>
      </section>

      {/* Section: Description (markdown) + Metadata */}
      <section className="space-y-4 border-b border-zinc-800 pb-4">
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Tabs defaultValue="edit" className="md:hidden">
            <TabsList>
              <TabsTrigger value="edit">Edit</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>
            <TabsContent value="edit">
              <Textarea
                id="description"
                value={description}
                onChange={(e) => { setDescription(e.target.value); }}
                rows={6}
                maxLength={2048}
                placeholder="A short polished blurb. Markdown supported."
              />
            </TabsContent>
            <TabsContent value="preview" className="rounded-md border border-zinc-800 p-3 text-zinc-200">
              <Suspense fallback={<p className="text-sm text-zinc-500">Loading…</p>}>
                <MarkdownView source={description} />
              </Suspense>
            </TabsContent>
          </Tabs>
          <div className="hidden md:grid md:grid-cols-2 md:gap-3">
            <Textarea
              id="description-desktop"
              value={description}
              onChange={(e) => { setDescription(e.target.value); }}
              rows={6}
              maxLength={2048}
              placeholder="A short polished blurb. Markdown supported."
              aria-hidden="true"
            />
            <div className="rounded-md border border-zinc-800 p-3 text-zinc-200">
              <Suspense fallback={<p className="text-sm text-zinc-500">Loading…</p>}>
                <MarkdownView source={description} />
              </Suspense>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="servings">Servings</Label>
            <Input
              id="servings"
              type="number"
              inputMode="numeric"
              min={1}
              max={999}
              step={1}
              value={servings}
              onChange={(e) => { setServings(e.target.value); }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="total-time">Total time (min)</Label>
            <Input
              id="total-time"
              type="number"
              inputMode="numeric"
              min={1}
              max={10080}
              step={1}
              value={totalTimeMin}
              onChange={(e) => { setTotalTimeMin(e.target.value); }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="active-time">Active time (min)</Label>
            <Input
              id="active-time"
              type="number"
              inputMode="numeric"
              min={1}
              max={10080}
              step={1}
              value={activeTimeMin}
              onChange={(e) => { setActiveTimeMin(e.target.value); }}
            />
            {totalTimeInt != null && activeTimeInt != null && activeTimeInt > totalTimeInt && (
              <p className="text-xs text-red-400 mt-1">Active time must be ≤ total time.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="difficulty">Difficulty</Label>
            <Select
              {...(difficulty ? { value: difficulty } : {})}
              onValueChange={(v) => { setDifficulty((v as "easy" | "medium" | "hard") ?? ""); }}
            >
              <SelectTrigger id="difficulty"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 md:col-span-4 space-y-1.5">
            <Label>Rating</Label>
            <Rating value={rating} onChange={setRating} />
          </div>
        </div>
      </section>

      {/* Section: Taxonomy */}
      <section className="space-y-3 border-b border-zinc-800 pb-4">
        <h2 className="text-zinc-50 font-medium">Tags &amp; categories</h2>
        <TaxonomyPicker value={taxonomy} onChange={setTaxonomy} />
        <p className="text-zinc-500 text-xs">
          Manage available tags / categories / cuisines / diet flags in{" "}
          <Link to="/settings/taxonomy" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2">
            Settings
          </Link>
          .
        </p>
      </section>

      {/* Section: Photos — edit mode only (recipe must exist before uploading) */}
      {isEdit && recipe && (
        <section className="space-y-3 border-b border-zinc-800 pb-4">
          <PhotoUploader
            householdId={householdId}
            recipe={recipe}
            onRecipeChange={(next) => {
              // Re-seed the version so a subsequent text-save uses the bumped
              // version and avoids a 409 conflict response.
              setVersion(next.version);
            }}
          />
        </section>
      )}

      {/* Section: Ingredients */}
      <section className="space-y-3 border-b border-zinc-800 pb-4">
        <h2 className="text-zinc-50 font-medium">Ingredients</h2>
        <IngredientList value={ingredients} onChange={setIngredients} />
      </section>

      {/* Section: Steps */}
      <section className="space-y-3 border-b border-zinc-800 pb-4">
        <h2 className="text-zinc-50 font-medium">Steps</h2>
        <StepList value={steps} onChange={setSteps} />
      </section>

      {/* Section: Notes */}
      <section className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={(e) => { setNotes(e.target.value); }}
          rows={4}
          maxLength={10240}
          placeholder="Scribble: technique tips, history, source attribution…"
        />
      </section>
    </>
  );
}
